import React, {
  useState,
  useMemo,
  useCallback,
  Fragment,
  useEffect,
} from "react";
import { Box, styled } from "@mui/material";
import { Responsive, WidthProvider, Layout, Layouts } from "react-grid-layout";
import TemplateCard from "../templateCard";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useNavigate } from "react-router-dom";
import { useTemplate } from "../../../context/templateContext";

export type ResizeHandle = "n" | "e" | "s" | "w" | "ne" | "nw" | "se" | "sw";

interface CardsGridProps {
  cards: DashCardsGraphics[];
  setCards: React.Dispatch<React.SetStateAction<DashCardsGraphics[]>>;
  firstLoading?: boolean;
  isCardLoading: React.MutableRefObject<Map<string, "loading" | "finished">>;
  handleDelete: (id: string) => void;
  duplicateCard?: (card: DashCardsGraphics) => void;
  editMode?: boolean;
  updateCard?: (updatedCard: DashCardsGraphics) => void;
  filtersInitialized: boolean;
  paginaId?: string;
  refreshKey: number;
}

const ResponsiveGridLayout = WidthProvider(Responsive);

const StyledGrid = styled(ResponsiveGridLayout)(({ theme }) => ({
  // estiliza a alça de redimensionamento
  "& .react-resizable-handle": {
    background: theme.palette.primary.main,
    width: 14,
    height: 14,
    borderBottomRightRadius: 7,
  },
  "& .react-resizable-handle::after": {
    background: theme.palette.primary.main,
  },
}));

const breakpoints = { lg: 1120, md: 1100, sm: 768, xs: 480, xxs: 0 };
const cols = { lg: 50, md: 24, sm: 12, xs: 8, xxs: 4 };
const rowHeight = 24;
const vMargin = 8;
const CardsGrid: React.FC<CardsGridProps> = ({
  cards,
  setCards,
  firstLoading,
  isCardLoading,
  handleDelete,
  duplicateCard,
  editMode = false, // default para false se não for passado
  updateCard,
  filtersInitialized,
  paginaId,
  refreshKey,
}) => {
  const [currentBreakpoint, setCurrentBreakpoint] =
    useState<keyof typeof cols>("lg");

  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);

  const navigate = useNavigate();
  const { setEditMode, onSave } = useTemplate();

  const resizeHandles: ResizeHandle[] = ["se"];
  const layoutBase: Layout[] = useMemo(
    () => cardsToLayout(cards, resizeHandles, currentBreakpoint),
    [cards, currentBreakpoint],
  );

  const layouts: Layouts = useMemo(() => {
    const clone = (arr: Layout[]) => arr.map((i) => ({ ...i }));
    return {
      lg: clone(layoutBase),
      md: clone(layoutBase),
      sm: clone(layoutBase),
      xs: clone(layoutBase),
      xxs: clone(layoutBase),
    };
  }, [layoutBase]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!hoveredCardId) return;
      if (event.key === "e") {
        setEditMode(true);
        navigate(`${window.location.pathname}/${hoveredCardId}`);
      }
      if (!editMode) return;
      // Ctrl+R to remove
      if (event.ctrlKey && event.key === "r") {
        event.preventDefault();
        handleDelete(hoveredCardId);
      }
      // Ctrl+D to duplicate
      if (event.ctrlKey && event.key === "d") {
        event.preventDefault();
        const card = cards.find((c) => c.id === hoveredCardId);
        if (card && duplicateCard) {
          duplicateCard(card);
        }
      }
      // Ctrl+S to save
      if (event.ctrlKey && event.key === "s") {
        event.preventDefault();
        if (onSave) onSave();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hoveredCardId, cards]);

  const persist = useCallback(
    (layoutNow: Layout[]) => {
      setCards((prev) =>
        prev.map((card, idx) => {
          const id = (card.id ?? idx).toString();
          const item = layoutNow.find((l) => l.i === id);
          if (!item) return card;
          return {
            ...card,
            positionLayout: { w: item.w, h: item.h, x: item.x, y: item.y },
          };
        }),
      );
    },
    [setCards],
  );

  const draggable = currentBreakpoint === "lg" || currentBreakpoint === "md";

  return (
    <Box width="100%">
      <StyledGrid
        layouts={layouts}
        breakpoints={breakpoints}
        cols={cols}
        rowHeight={rowHeight}
        margin={[vMargin, vMargin]}
        containerPadding={[0, 0]}
        compactType="vertical"
        useCSSTransforms
        isResizable={draggable && editMode}
        isDraggable={draggable && editMode}
        resizeHandles={resizeHandles} /* igual ao Grafana */
        onBreakpointChange={(bp) =>
          setCurrentBreakpoint(bp as keyof typeof cols)
        }
        onDragStop={persist}
        onResizeStop={persist}
        draggableCancel=".cancelSelectorName"
        style={{}}
      >
        {cards.map((card, idx) => (
          <Box
            key={card.id ?? idx}
            width="100%"
            height="100%"
            onMouseEnter={() => setHoveredCardId(card.id ?? null)}
            onMouseLeave={() => setHoveredCardId(null)}
          >
            <Fragment
              key={`${card.id}-${card.positionLayout?.w}-${card.positionLayout?.h}`}
            >
              <TemplateCard
                index={idx}
                card={card}
                filtersInitialized={filtersInitialized}
                firstLoading={firstLoading}
                key={card.id}
                editMode={editMode}
                isEditing={false}
                handleDelete={handleDelete}
                duplicateCard={duplicateCard}
                updateCard={updateCard}
                paginaId={paginaId}
                refreshKey={refreshKey}
              />
            </Fragment>
          </Box>
        ))}
      </StyledGrid>
    </Box>
  );
};

export default CardsGrid;

function cardsToLayout(
  cards: DashCardsGraphics[],
  handles: ResizeHandle[],
  currentBreakpoint: keyof typeof cols,
): Layout[] {
  return cards.map((card, idx) => {
    let h = 5;
    let w = cols[currentBreakpoint];
    let x = 0;
    let y = idx * h;

    const isDesktop = currentBreakpoint === "lg" || currentBreakpoint === "md";

    if (
      isDesktop &&
      card.positionLayout?.w != null &&
      card.positionLayout?.h != null &&
      card.positionLayout?.x != null &&
      card.positionLayout?.y != null
    ) {
      h = card.positionLayout.h;
      w = card.positionLayout.w;
      x = card.positionLayout.x;
      y = card.positionLayout.y;
    } else {
      h = card.height ? Math.round(Number(card.height) / 30) || 5 : 5;
      w = cols[currentBreakpoint];
      x = 0;
    }

    return {
      i: (card.id ?? idx).toString(),
      x,
      y,
      w,
      h,
      handles,
    };
  });
}
